export const homepageRedesign = {
    slug: 'homepage-redesign',
    no: '01',
    type: 'rich',
    title: 'Engagedly Homepage Redesign',
    subtitle: 'Engage with Engagedly',
    category: 'UX Design',
    sourceUrl: 'https://himanshutanty.com/portfolio/homepage-redesign/',
    cover: 'https://framerusercontent.com/images/wv3HYHKNQzH1y5keUPJVlvQgt0.png',
    client: 'Engagedly',
    timeline: '4 Weeks',
    responsibilities: [
        'Requirement gathering',
        'Design Audit',
        'Research',
        'Wireframing',
        'User testing & reiteration',
        'Hand off and Dev queries',
    ],
    overview:
        'Engagedly is a People + Strategy platform whose 16 module overview page had grown transactional and hard to navigate. I redesigned the landing experience into a single value-based Homepage, organised around the Engage, Enable and Execute strategy, so users instantly understand where to go, what matters today, and how to act.',
    prototype:
        'https://www.figma.com/proto/rCj2Suk9pBgZ16K7kb2wEa/Homepage?page-id=1401%3A69782&node-id=1401-94879&viewport=862%2C1976%2C0.14&scaling=scale-down-width&starting-point-node-id=1401%3A79518&hide-ui=1',
    blocks: [
        // ── Project description (from PDF) ─────────────────────────
        {
            kind: 'section',
            heading: 'About the project',
            subheading: 'Challenge · Engage with Engagedly',
            paragraph:
                'Engagedly is a People + Strategy platform. Its Overview page had grown transactional and module-driven; users surfed 16 modules and still did not know what to do, drifting from the E3 strategy the product is built on: Engage, Enable & Execute. The redesign replaces it with a single, value-based Homepage, organised around what matters now.',
        },
        {
            kind: 'section',
            heading: 'Goals and objectives',
        },
        {
            kind: 'grid-cards',
            items: [
                {
                    title: 'Give control to the users',
                    body: 'Admin can configure the homepage and make it flexible enough for the employees to prioritize the information accordingly.',
                },
                {
                    title: 'Create opportunities',
                    body: 'Action-based triggers for the users to interact with, rather than going to the individual module to discover.',
                },
                {
                    title: 'Manager on a mission',
                    body: 'A space for the manager to be able to manage their direct reports and nudge them on a timely basis based on their KPIs.',
                },
            ],
        },
        {
            kind: 'section',
            heading: 'Persona based flow',
        },
        {
            kind: 'grid-cards',
            items: [
                {
                    title: 'Admin',
                    body: 'Configure what an employee can see: the ability to add or remove widgets, make quick announcements, and conduct polls.',
                },
                {
                    title: 'Managers',
                    body: 'Organize and curate the information as per their need. Focus individually on each direct report and take the necessary action to help or nudge them in the right direction.',
                },
                {
                    title: 'Employees',
                    body: 'Organizing information as per their convenience. Getting insights on critical metrics and taking the necessary action.',
                },
            ],
        },
        // ── Design-Thinking walk-through ────────────────────────────
        {
            kind: 'tabs',
            tabs: [
                // ── Empathise ────────────────────────────────────────
                {
                    label: 'Empathise',
                    summary:
                        'Current-page analysis, competitor benchmarks and a participatory workshop to hear how users really use the page.',
                    blocks: [
                        {
                            kind: 'phase-hero',
                            phase: 'empathise',
                            number: '01',
                            title: 'Empathise',
                            items: [
                                'Current page analysis',
                                'Competitor analysis',
                                'Participatory Design Workshop',
                                'Unsolicited feedback',
                            ],
                        },
                        {
                            kind: 'section',
                            heading: 'Current page Analysis',
                            paragraph:
                                'Since I use this platform on a daily basis, I started with the analysis of the current Home page and documented the issues.',
                        },
                        {
                            kind: 'image',
                            src: '/api/files/e2539730-516d-4575-a89a-071205ef3de7',
                        },
                        {
                            kind: 'section',
                            heading: 'Competitor Analysis',
                            paragraph:
                                'We audited close and indirect competitors, Officevibe, 15Five, Reflektive, Fellow, Betterworks, Cornerstone, Culture Amp, Workday, Leapsome, MS Viva and Lattice, mapping their major features against ours to surface the gaps worth closing.',
                        },
                        {
                            kind: 'image',
                            src: '/api/files/17adc179-b321-4a67-ac97-ec98ac0d66d3',
                        },
                        {
                            kind: 'section',
                            heading: 'Participatory Design Workshop',
                            paragraph:
                                'Agenda, Rediscovering Home Page. Objective, Work together on a shared vision and facilitate solutions with the users instead of prescribing it to them. This inter-department workshop was conducted to get feedback, identify pain points and generate ideas via a rough sketching exercise.',
                        },
                        {
                            kind: 'image',
                            src: '/api/files/5df6b306-e190-42ce-b3fd-a6c5c443c1c5',
                        },
                        {
                            kind: 'grid-cards',
                            items: [
                                { title: 'Empathy mapping', body: 'Aligning the team on user pains and gains before ideating.' },
                                { title: 'Explore new ideas', body: 'Gave them a chance to explore their thoughts and explore new ideas.' },
                                { title: 'Rough sketching', body: 'Collectively draw a rough sketch of the home page.' },
                            ],
                        },
                        {
                            kind: 'section',
                            heading: 'Empathy Mapping',
                            paragraph:
                                'Using the empathy map framework, we explored their thoughts, feelings and behaviours around the home page. Each participant had space to speak, react and sketch, Wendy sits at the centre because her Says / Thinks / Does / Feels showed up most often across both workshop teams.',
                        },
                        {
                            kind: 'engagedly-empathy-map',
                        },
                        {
                            kind: 'section',
                            heading: 'Rough sketching',
                            paragraph:
                                'The two participant teams collectively drew a rough sketch of what they wished the home page looked like, Team A led with a widget-driven layout (Attention · Org updates · Team outing · Try it out · Launch/Promotions · Graphical Representation · Live Vote · Suggestions), Team B leaned into a Resource Center + Announcements strip + Fun event carousel + Onboarding checklist + Manage/Add widgets pattern.',
                        },
                        {
                            kind: 'engagedly-rough-sketch',
                        },
                        {
                            kind: 'section',
                            heading: 'Post-workshop Analysis',
                            paragraph:
                                'All sticky notes from the workshop were consolidated into three buckets, Problems (what breaks today on the overview page), Statements (how users talk about the page in their own words) and Expectations (what they would like it to become). This board became the north-star reference for every design decision that followed.',
                        },
                        {
                            kind: 'engagedly-workshop-board',
                        },
                        {
                            kind: 'section',
                            heading: 'Unsolicited feedback',
                            paragraph:
                                'I collected a few of the comments from G2, calls with AMs, support tickets and emails. Here are a few:',
                        },
                        {
                            kind: 'quotes',
                            items: [
                                'Horrible UX. Finding anything within the platform is a heavy cognitive load since it is so user un-friendly.',
                                "Very complicated. It's not always clear where to go for information or if the information is visible to others.",
                                'It can feel awkward to maneuver; there are too many options to get to one item that confuses the user.',
                                'They also updated the program to make it a lot user-friendly too! Clearing confusing unnecessary additional steps to complete what was once a simple task.',
                                "The software improves on the UX especially for employees or managers who didn't use the software on a weekly basis. I really enjoyed.",
                                'Too many ways to get to the qualifying section. The menu is not intuitive.',
                            ],
                        },
                    ],
                },
                // ── Define ───────────────────────────────────────────
                {
                    label: 'Define',
                    summary:
                        'Turning the research into a clear user persona and a sharp problem to solve.',
                    blocks: [
                        {
                            kind: 'phase-hero',
                            phase: 'define',
                            number: '02',
                            title: 'Define',
                            items: [
                                'User persona',
                                'Problem framing',
                            ],
                        },
                        {
                            kind: 'section',
                            heading: 'User persona',
                            paragraph:
                                'Based on the workshop and comments from stakeholders, regular users and clients, I created a user persona which helped me make sense of the data and user needs.',
                        },
                        {
                            kind: 'persona',
                            image: 'https://framerusercontent.com/images/OpXHuB9pQ5m9wRlRbQ1YgQXJm04.png',
                            name: 'Hi, I am Wendy Horton.',
                            intro:
                                'Wendy is a Sales Associate who works remotely from her home and travels once a month for her work. In her spare time, she likes to research on her competitors and stay updated with the current trends around the globe.',
                            stats: [
                                { label: 'Age', value: '40' },
                                { label: 'Marital Status', value: 'Single' },
                                { label: 'Location', value: 'San Fransisco, California' },
                                { label: 'Education', value: 'MBA' },
                                { label: 'Job Title', value: 'Sales Associate' },
                                { label: 'Traits', value: 'Extrovert · Solo-player · Organized · Fast-learner' },
                            ],
                            goals: [
                                'Quickly access the relevant information and resources to do her job efficiently',
                                'Navigate smoothly through the platform and find what she needs to do without getting lost',
                                'Stay up-to-date with company / industry news and trends',
                            ],
                            wishlist: [
                                'Get her tasks done quickly without the need to look for things which is time-consuming',
                                'Keep her information well organized and prioritize only what is important',
                                'Easy access to sales tools and resources, such as CRM software and marketing materials',
                            ],
                            frustrations: [
                                'Slow-loading pages that hinder her ability to cater to urgent tasks',
                                'Confusing navigation that makes it difficult to find what she needs',
                                'Lack of regular updates on the company',
                            ],
                        },
                        {
                            kind: 'engagedly-traits',
                        },
                    ],
                },
                // ── Ideate ───────────────────────────────────────────
                {
                    label: 'Ideate',
                    summary:
                        'Brainstorming navigation, mind maps, sketches and the first wireframes for the new homepage.',
                    blocks: [
                        {
                            kind: 'phase-hero',
                            phase: 'ideate',
                            number: '03',
                            title: 'Ideate',
                            items: [
                                'Brainstorm',
                                'Mind mapping',
                                'Sketches',
                                'Wireframes',
                            ],
                        },
                        {
                            kind: 'section',
                            heading: 'Brainstorm',
                            subheading: 'Navigation',
                            paragraph:
                                'Initial brainstorming sessions with the team and stakeholders led to a three-tab approach where all the modules which are currently placed in the top navigation are grouped and placed in three tabs, Home, Grow and Community.',
                        },
                        {
                            kind: 'image',
                            src: 'https://framerusercontent.com/images/3nEKgskWRGPWs6psK5LfQQIN7U.png',
                        },
                        {
                            kind: 'section',
                            subheading: 'Widget sorting',
                            paragraph:
                                'Widgets were sorted based on the behaviour, which one will be fixed and which one will be fluid, as the customization feature was on the table.',
                        },
                        {
                            kind: 'image',
                            src: 'https://framerusercontent.com/images/HjU8Wrvc1X7Lli8gzwEcE1OpE9A.png',
                        },
                        {
                            kind: 'section',
                            heading: 'Mind mapping',
                            paragraph:
                                'Home → Navigation (Dashboard, Goals, Feedback); Grow → Check-in, 360, Performance; Community → Engagement, Social, Recognition, Learning, Directory, Help, My Circle.',
                        },
                        {
                            kind: 'image',
                            src: 'https://framerusercontent.com/images/cOMi1eLHbgq1cNQ8xyqBK6bho.png',
                        },
                        {
                            kind: 'section',
                            heading: 'Sketches',
                            paragraph:
                                'Sketching mainly involved which widgets will be placed where and how the layout of the entire homepage will accommodate them.',
                        },
                        {
                            kind: 'image',
                            src: 'https://framerusercontent.com/images/5hJ0CIEKNsLcmtoGOjNKqbbXWw.png',
                        },
                        {
                            kind: 'image',
                            src: 'https://framerusercontent.com/images/Cl7UjqOL2FpHyHsEIzEr7dfXZvo.png',
                        },
                        {
                            kind: 'section',
                            heading: 'Wireframes',
                        },
                        {
                            kind: 'image',
                            src: 'https://framerusercontent.com/images/CLEUGkBVTFbCZFgh6Vjw6IMlwQ.png',
                        },
                    ],
                },
                // ── Prototype ────────────────────────────────────────
                {
                    label: 'Prototype',
                    summary:
                        'Design iterations, user testing and the final, improved homepage.',
                    blocks: [
                        {
                            kind: 'phase-hero',
                            phase: 'prototype',
                            number: '04',
                            title: 'Prototype',
                            items: [
                                'Design iterations',
                                'User testing',
                                'Final home page',
                            ],
                        },
                        {
                            kind: 'section',
                            heading: 'Design Iterations',
                            paragraph:
                                'Three passes across the landing page, customization panel and feedback surface, each round tightened the layout and interaction patterns.',
                        },
                        {
                            kind: 'image',
                            src: 'https://framerusercontent.com/images/M7YcEw3Ap5dxBtlLKlng3L7M.png',
                        },
                        {
                            kind: 'image',
                            src: 'https://framerusercontent.com/images/NjYG9G4SPttUXUkFls4qra2u8PU.png',
                        },
                        {
                            kind: 'image',
                            src: 'https://framerusercontent.com/images/ssllvPWc1UjNMjjYsFrGtO1Uvxs.png',
                        },
                        {
                            kind: 'section',
                            heading: 'User testing',
                            paragraph:
                                'We conducted user testing on the final version of the Homepage. Post the test, feedback was consolidated and accordingly the changes were made onto the final design.',
                        },
                        {
                            kind: 'image',
                            src: 'https://framerusercontent.com/images/Kmmkq2RF7hfuK1OvoyY5qKG8Zcw.png',
                        },
                        {
                            kind: 'section',
                            heading: 'Home Page',
                            paragraph:
                                'Presenting the new and improved homepage.',
                        },
                        {
                            kind: 'image',
                            src: '/api/files/cf61ec11-e88f-4715-8926-6d765aaeaa5a',
                        },
                    ],
                },
                // ── Deliver (Test + Principles + Excluded features) ──
                {
                    label: 'Deliver',
                    summary:
                        'A/B testing against Lattice and Culture Amp, the principles behind the design, and the features we deferred.',
                    blocks: [
                        {
                            kind: 'phase-hero',
                            phase: 'deliver',
                            number: '05',
                            title: 'Deliver',
                            items: [
                                'A/B testing against Lattice & Culture AMP',
                                'Design principles behind the redesign',
                                'Excluded features & rationale',
                            ],
                        },
                        {
                            kind: 'section',
                            heading: 'A/B Test',
                        },
                        {
                            kind: 'meta-list',
                            items: [
                                {
                                    label: 'Agenda',
                                    value: 'To identify any problems or flaws in the product, and make recommended changes and adjustments before handing off to development.',
                                },
                                {
                                    label: 'Participants',
                                    value: 'Tested 3 designs with 50 participants. To ensure users base their decisions solely on their first impression of the visual designs, content and navigation style were standardized on all screens, and logos were eliminated.',
                                },
                                {
                                    label: 'Result',
                                    value: 'Engagedly vs. Lattice, 75% voted Engagedly, 25% Lattice. Engagedly vs. Culture AMP, 62.5% voted Engagedly, 37.5% Culture AMP. Among first preference: 18% Lattice, 30% Culture AMP, 52% Engagedly. We went ahead with implementing the new design.',
                                },
                            ],
                        },
                        {
                            kind: 'image',
                            src: '/api/files/fa553da1-4d69-44ac-8c56-3e97b5c662ec',
                        },
                        {
                            kind: 'section',
                            heading: 'Design principles',
                            paragraph:
                                'Three principles that shaped the new homepage.',
                        },
                        {
                            kind: 'principle',
                            heading: 'Mere-exposure effect',
                            image: 'https://framerusercontent.com/images/gcTXPPUQLS9GliYZhdTk19qXgus.png',
                            body: 'The familiarity principle states that people tend to naturally gravitate towards products they are familiar with, especially when high-stake decisions are involved. Colour psychology research tells us that the universal favorite color is blue, irrespective of gender. Even after stacking enterprise apps under the top three favorite colours, Blue, Purple and Green, the difference between blue and purple is small, but men don\u2019t seem to favour purple. So going ahead, Engagedly will be part of "Team Blue."',
                        },
                        {
                            kind: 'principle',
                            heading: 'Build emotional connections',
                            image: 'https://framerusercontent.com/images/Ja2Ph0Yz3wV342JnLE1hvuxHwo.png',
                            body: 'While blue is the universal favorite, some people prefer other colours. To ensure their preferences are catered to, many modern apps provide theming capabilities. Therefore, going ahead, Engagedly\u2019s default will be blue and users will have the option to customize it to better connect with their emotions.',
                        },
                        {
                            kind: 'principle',
                            heading: 'Information density',
                            image: 'https://framerusercontent.com/images/o0i98ka5q9CWSnQn6kImaAodpA.png',
                            body: 'The apps in the market depict a trend with the density of information present on the user\u2019s screen, Employee screens are lightweight, Managers are medium, and Admins are heavy with content. We have been following and will continue to follow the same with the new homepage design.',
                        },
                        {
                            kind: 'section',
                            heading: 'Not Included Features',
                            paragraph:
                                'During the project, we assessed features that would either need to be deferred to the next phase or omitted entirely.',
                        },
                        {
                            kind: 'excluded',
                            items: [
                                {
                                    title: 'Banner Announcement',
                                    image: 'https://framerusercontent.com/images/2w9IcFSTGNtiPxdDXkE9iVgryo.png',
                                    effort: 'When considering the Announcements feature, we recognized that the development effort required would be substantial.',
                                    alt: 'While creating a social post, the Admin can pin the post as Announcement which will then reflect on the Homepage as a Banner.',
                                },
                                {
                                    title: 'Activities',
                                    image: 'https://framerusercontent.com/images/QhFvibMehUOXEMdULefLY4ydEsg.png',
                                    effort: 'Recent Activities was identified as High Effort, High Value because it significantly saves users time in terms of navigation and finding what they need.',
                                    alt: 'Deferred to next phase.',
                                },
                                {
                                    title: 'Poll',
                                    image: 'https://framerusercontent.com/images/SFGElPS9pVixHZdsAiZkLZOyVc.png',
                                    effort: 'Poll was identified as High Effort, Low Value.',
                                    alt: 'At present, the Surveys module can be utilized, and questions can be designated as "Add to Poll." This approach saves time that would have been required to develop a completely separate stand-alone feature.',
                                },
                            ],
                        },
                    ],
                },
            ],
        },
    ],
    next: { slug: 'my-communication', title: 'My Communications' },
};
